import { useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { routes } from 'commons/constants/routes'
import { Request } from 'commons/utils/request'
import { Icon, IconName, IconSize, Info, Link, PageSectionPrimary, TextareaInput, Toast } from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { Col, Col2x, Row } from 'ui/themes/globalStyles'

import { handlerServerError } from '@orbit/errors'

import { useHRRContext } from '../../context'
import { HRR_DETAILS_KEY } from '../../query'
import { UpdatePeriodDialog } from './components'

export const GeneralInformation = () => {
  const { data } = useHRRContext()
  const {
    infoRelationshipName,
    infoPeriod,
    infoStatus,
    infoNotes,
    highRiskReviewId,
    periodStartDate,
    periodEndDate,
    infoRelationshipId
  } = data

  const [notes, setNotes] = useState(infoNotes)
  const [showPeriodDialog, setShowPeriodDialog] = useState(false)

  const queryClient = useQueryClient()

  const { mutate: saveNotes } = useMutation<void, Error, { id: number; notes: string }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.hrrSaveNotes, opts),
    onSuccess: () => {
      Toast.successSmall('Notes have been saved.')
      queryClient.invalidateQueries({ queryKey: [HRR_DETAILS_KEY] })
    },
    onError: (error) => handlerServerError(error)
  })

  const saveNotesHandler = () => {
    saveNotes({ id: highRiskReviewId, notes })
  }

  return (
    <PageSectionPrimary title="General Information" face="outlined" anchor="general">
      <Row>
        <Col>
          <Info label="Relationship Name">
            <Link to={routes.bank.relationships.details} params={{ id: infoRelationshipId.toString() }}>
              {infoRelationshipName}
            </Link>
          </Info>
        </Col>
        <Col>
          <Info label="Period">
            <div style={{ display: 'flex', gap: '10px' }}>
              {showPeriodDialog && (
                <UpdatePeriodDialog
                  id={highRiskReviewId}
                  startDate={periodStartDate}
                  endDate={periodEndDate}
                  onClose={() => setShowPeriodDialog(false)}
                />
              )}
              {infoPeriod}
              <span style={{ cursor: 'pointer' }}>
                <Icon name={IconName.EDIT} size={IconSize.S} onClick={() => setShowPeriodDialog(true)} />
              </span>
            </div>
          </Info>
        </Col>
      </Row>
      <Row>
        <Col2x>
          <Info label="Status">{infoStatus}</Info>
        </Col2x>
      </Row>
      <Row>
        <Col2x>
          <Info label="Notes">
            <TextareaInput value={notes} onChange={(v: string) => setNotes(v)} />
          </Info>
        </Col2x>
      </Row>
      <Row>
        <Col2x>
          <Button face="tertiary" data-qa="New High-Risk Review" onClick={saveNotesHandler}>
            Save notes
          </Button>
        </Col2x>
      </Row>
    </PageSectionPrimary>
  )
}
